import React, {useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {COUNT_BADGE_PLACEMENT, CountBadge, DropdownList, FlexRow, Icon, PillButton} from '@forecast-it/design-system';
import {AGENTIC_WORKERS_MESSAGE} from './agenticWorkersMessages';
import type {AgentPage, AgentRegistration} from './agentDeclaration';
import {ensureAgentsRegistered, getAgentsForPage} from './agentRegistry';
import {useAgentRunState} from './agentRunState';
import {canSeeAgent} from './agentVisibility';
import {ScheduleDrawer} from '../scheduling/ScheduleDrawer';

ensureAgentsRegistered();

const MENU_OFFSET_PIXELS = 4;

// Positioned so the waiting badge can sit on the pill's top-right corner.
const AgentControl = styled(FlexRow)`
	position: relative;
`;

const MenuItemRow = ({children}: {children: React.ReactNode}): JSX.Element => (
	<FlexRow alignItems={'center'} gap={'s'}>
		{children}
	</FlexRow>
);

type AgentButtonProps = {
	registration: AgentRegistration;
	page: AgentPage;
	isScheduleOpen: boolean;
	onOpenSchedule: () => void;
	onCloseSchedule: () => void;
};

// One agent's pill and menu. A component of its own because each agent holds its own run state. Its
// schedule drawer's open state is owned by the toolbar, so only one agent's drawer is open at a time.
const AgentButton = ({registration, page, isScheduleOpen, onOpenSchedule, onCloseSchedule}: AgentButtonProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {declaration} = registration;
	const {isRunning, waitingCount, startRun, stopRun} = useAgentRunState(registration, page);
	const [isMenuOpen, setMenuOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);

	const agentName = formatMessage(declaration.name);
	const waitingAccessibleName = formatMessage(
		{id: AGENTIC_WORKERS_MESSAGE.BADGE_WAITING},
		{
			count: waitingCount,
			resultSingular: formatMessage(declaration.resultNoun.singular),
			resultPlural: formatMessage(declaration.resultNoun.plural),
		}
	);

	const runFromMenu = (): void => {
		setMenuOpen(false);
		startRun();
	};

	const openSchedule = (): void => {
		setMenuOpen(false);
		onOpenSchedule();
	};

	// The Past Runs drawer is not built yet, so choosing it only closes the menu.
	const closeMenu = (): void => {
		setMenuOpen(false);
	};

	// The stop control disappears once the run stops, so focus is handed to the trigger instead of
	// falling back to the page.
	const stopFromButton = (): void => {
		setMenuOpen(false);
		stopRun();
		triggerRef.current?.focus();
	};

	return (
		<AgentControl flex={'0 0 auto'} data-cy={`agent-button-${declaration.id}`}>
			<DropdownList open={isMenuOpen} onOpenChange={setMenuOpen}>
				<DropdownList.Trigger>
					<PillButton
						ref={triggerRef}
						icon={'sparkle'}
						rightIcon={isRunning ? undefined : 'chevronDown'}
						aria-label={
							isRunning
								? formatMessage({id: AGENTIC_WORKERS_MESSAGE.BUTTON_WORKING_NAME}, {name: agentName})
								: undefined
						}
						trailingAction={
							isRunning
								? {
										icon: 'stopFilled',
										'aria-label': formatMessage(
											{id: AGENTIC_WORKERS_MESSAGE.BUTTON_STOP},
											{name: agentName}
										),
										onClick: stopFromButton,
										'data-cy': 'agent-button-stop',
								  }
								: undefined
						}
						data-cy={'agent-button-trigger'}
					>
						{isRunning ? formatMessage({id: AGENTIC_WORKERS_MESSAGE.BUTTON_WORKING}) : agentName}
					</PillButton>
				</DropdownList.Trigger>
				<DropdownList.Content align={'start'} side={'bottom'} offset={MENU_OFFSET_PIXELS} portalled={true}>
					<DropdownList.Item disabled={isRunning} onClick={runFromMenu} data-cy={'agent-menu-run'}>
						<MenuItemRow>
							<Icon icon={'sparkle'} size={'s'} newColor={isRunning ? 'disabled' : undefined} />
							{isRunning
								? formatMessage({id: AGENTIC_WORKERS_MESSAGE.MENU_RUNNING})
								: formatMessage({id: AGENTIC_WORKERS_MESSAGE.MENU_RUN}, {name: agentName})}
						</MenuItemRow>
					</DropdownList.Item>
					<DropdownList.Item onClick={openSchedule} data-cy={'agent-menu-schedule'}>
						<MenuItemRow>
							<Icon icon={'clock'} size={'s'} />
							{formatMessage({id: AGENTIC_WORKERS_MESSAGE.MENU_SCHEDULE}, {name: agentName})}
						</MenuItemRow>
					</DropdownList.Item>
					<DropdownList.Item onClick={closeMenu} data-cy={'agent-menu-past-runs'}>
						<MenuItemRow>
							<Icon icon={'list'} size={'s'} />
							{formatMessage({id: AGENTIC_WORKERS_MESSAGE.MENU_PAST_RUNS}, {name: agentName})}
							<CountBadge
								count={waitingCount}
								aria-label={waitingAccessibleName}
								placement={COUNT_BADGE_PLACEMENT.INLINE}
							/>
						</MenuItemRow>
					</DropdownList.Item>
				</DropdownList.Content>
			</DropdownList>
			<CountBadge count={waitingCount} aria-label={waitingAccessibleName} placement={COUNT_BADGE_PLACEMENT.CORNER} />
			<ScheduleDrawer
				isOpen={isScheduleOpen}
				onClose={onCloseSchedule}
				name={agentName}
				consumer={declaration.schedulingTarget}
			/>
		</AgentControl>
	);
};

type AgentToolbarButtonsProps = {
	page: AgentPage;
};

export const AgentToolbarButtons = ({page}: AgentToolbarButtonsProps): JSX.Element | null => {
	// The agent whose schedule drawer is open. Opening another agent's replaces it.
	const [scheduleAgentId, setScheduleAgentId] = useState<string | null>(null);
	const visibleAgents = getAgentsForPage(page).filter(registration => canSeeAgent(registration.declaration));
	if (visibleAgents.length === 0) {
		return null;
	}
	return (
		<FlexRow alignItems={'center'} gap={'s'} flex={'0 0 auto'} data-cy={'agent-toolbar-buttons'}>
			{visibleAgents.map(registration => {
				const agentId = registration.declaration.id;
				return (
					<AgentButton
						key={agentId}
						registration={registration}
						page={page}
						isScheduleOpen={scheduleAgentId === agentId}
						onOpenSchedule={() => setScheduleAgentId(agentId)}
						onCloseSchedule={() => setScheduleAgentId(current => (current === agentId ? null : current))}
					/>
				);
			})}
		</FlexRow>
	);
};
